import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  BriefcaseIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  ClockIcon,
  BuildingOfficeIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import Navigation from '../components/common/Navigation';
import Footer from '../components/common/Footer';

interface JobPosting {
  _id: string;
  title: string;
  slug: string;
  department: string;
  location: {
    type: string;
    city?: string;
    state?: string;
  };
  employmentType: string;
  experienceLevel: string;
  salaryRange?: {
    min: number;
    max: number;
    currency: string;
  };
  summary: string;
  featured: boolean;
  urgent: boolean;
  publishedAt: string;
}

const Careers: React.FC = () => {
  const [jobs, setJobs] = useState<JobPosting[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<JobPosting[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [selectedLevel, setSelectedLevel] = useState<string>('all');

  const departments = ['all', 'Engineering', 'Product', 'Sales', 'Marketing', 'Operations', 'Finance', 'Legal', 'Human Resources'];
  const locationTypes = ['all', 'office', 'remote', 'hybrid'];
  const experienceLevels = ['all', 'entry', 'mid', 'senior', 'lead', 'executive'];

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    filterJobs();
  }, [jobs, selectedDepartment, selectedLocation, selectedLevel]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/careers/jobs?status=active');
      setJobs(response.data.jobs);
    } catch (error) {
      console.error('Error fetching job postings:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterJobs = () => {
    let filtered = [...jobs];

    if (selectedDepartment !== 'all') {
      filtered = filtered.filter(job => job.department === selectedDepartment);
    }

    if (selectedLocation !== 'all') {
      filtered = filtered.filter(job => job.location.type === selectedLocation);
    }

    if (selectedLevel !== 'all') {
      filtered = filtered.filter(job => job.experienceLevel === selectedLevel);
    }

    // Sort featured and urgent jobs first
    filtered.sort((a, b) => {
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      if (a.urgent && !b.urgent) return -1;
      if (!a.urgent && b.urgent) return 1;
      return 0;
    });

    setFilteredJobs(filtered);
  };

  const formatSalary = (salaryRange?: JobPosting['salaryRange']) => {
    if (!salaryRange) return 'Competitive';
    const { min, max, currency } = salaryRange;
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    return `${formatter.format(min)} - ${formatter.format(max)}`;
  };

  const formatLocation = (location: JobPosting['location']) => {
    if (location.type === 'remote') return 'Remote';
    if (location.city && location.state) return `${location.city}, ${location.state}`;
    return location.type.charAt(0).toUpperCase() + location.type.slice(1);
  };

  const formatLevel = (level: string) => {
    const levels: Record<string, string> = {
      entry: 'Entry Level',
      mid: 'Mid Level',
      senior: 'Senior Level',
      executive: 'Executive'
    };
    return levels[level] || level;
  };

  const formatEmploymentType = (type: string) => {
    return type.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const companyValues = [
    {
      icon: BuildingOfficeIcon,
      title: 'Innovation First',
      description: 'We push boundaries and challenge the status quo in equity financing'
    },
    {
      icon: BuildingOfficeIcon,
      title: 'Collaborative Culture',
      description: 'We believe in the power of teamwork and diverse perspectives'
    },
    {
      icon: BuildingOfficeIcon,
      title: 'Impact Driven',
      description: 'We\'re committed to making capital markets more accessible'
    },
    {
      icon: BuildingOfficeIcon,
      title: 'Growth Mindset',
      description: 'We invest in your development and celebrate continuous learning'
    }
  ];

  const benefits = [
    'Competitive salary and equity compensation',
    'Comprehensive health, dental, and vision coverage',
    'Flexible PTO and sabbatical opportunities',
    'Remote-first work environment',
    '$5,000 annual learning & development budget',
    '401(k) with company matching',
    'Parental leave program',
    'Wellness and mental health support'
  ];

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-financial">
        {/* Hero Section */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <motion.h1 
              className="text-5xl font-bold text-primary-950 mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Join Our Mission
            </motion.h1>
            <motion.p 
              className="text-xl text-primary-700 max-w-3xl mx-auto mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Help us revolutionize equity financing and make capital markets more accessible 
              for growing companies worldwide.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <a
                href="#openings"
                className="inline-flex items-center gap-2 px-8 py-4 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
              >
                View Open Positions
                <ChevronRightIcon className="h-5 w-5" />
              </a>
            </motion.div>
          </div>
        </section>

        {/* Company Values */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-primary-950 text-center mb-12">
              Our Values
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {companyValues.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <value.icon className="h-8 w-8 text-primary-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-primary-950 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-gray-600">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-primary-950 mb-6">
                  Benefits & Perks
                </h2>
                <p className="text-lg text-primary-700 mb-8">
                  We believe in taking care of our team. That's why we offer comprehensive 
                  benefits that support your health, wealth, and happiness.
                </p>
                <ul className="space-y-3">
                  {benefits.map((benefit, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-start gap-3"
                    >
                      <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                      </div>
                      <span className="text-gray-700">{benefit}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=400&fit=crop"
                  alt="Team collaboration"
                  className="rounded-lg shadow-lg"
                />
                <div className="absolute -bottom-6 -right-6 bg-primary-600 text-white p-6 rounded-lg shadow-lg">
                  <p className="text-3xl font-bold">4.8/5</p>
                  <p className="text-sm">Employee Satisfaction</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Job Openings */}
        <section id="openings" className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-primary-950 text-center mb-12">
              Open Positions
            </h2>

            {/* Filters */}
            <div className="bg-white p-6 rounded-lg shadow-md mb-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Department
                  </label>
                  <select
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {departments.map(dept => (
                      <option key={dept} value={dept}>
                        {dept === 'all' ? 'All Departments' : dept}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <select
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {locationTypes.map(loc => (
                      <option key={loc} value={loc}>
                        {loc === 'all' ? 'All Locations' : loc.charAt(0).toUpperCase() + loc.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Experience Level
                  </label>
                  <select
                    value={selectedLevel}
                    onChange={(e) => setSelectedLevel(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {experienceLevels.map(level => (
                      <option key={level} value={level}>
                        {level === 'all' ? 'All Levels' : formatLevel(level)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Job Listings */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              </div>
            ) : filteredJobs.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg">
                <BriefcaseIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No positions match your criteria.</p>
                <p className="text-sm text-gray-400 mt-2">Try adjusting your filters or check back later.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredJobs.map((job, index) => (
                  <motion.div
                    key={job._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow ${
                      job.featured ? 'ring-2 ring-primary-500' : ''
                    }`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Link to={`/careers/${job.slug}`}>
                            <h3 className="text-xl font-semibold text-primary-950 hover:text-primary-600 transition-colors">
                              {job.title}
                            </h3>
                          </Link>
                          {job.featured && (
                            <span className="px-2 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                              Featured
                            </span>
                          )}
                          {job.urgent && (
                            <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                              Urgent
                            </span>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
                          <div className="flex items-center gap-1">
                            <BriefcaseIcon className="h-4 w-4" />
                            <span>{job.department}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <MapPinIcon className="h-4 w-4" />
                            <span>{formatLocation(job.location)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <ClockIcon className="h-4 w-4" />
                            <span>{formatEmploymentType(job.employmentType)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <BuildingOfficeIcon className="h-4 w-4" />
                            <span>{formatLevel(job.experienceLevel)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <CurrencyDollarIcon className="h-4 w-4" />
                            <span>{formatSalary(job.salaryRange)}</span>
                          </div>
                        </div>

                        <p className="text-gray-600 line-clamp-2">
                          {job.summary}
                        </p>
                      </div>

                      <div className="flex items-center gap-3">
                        <Link
                          to={`/careers/${job.slug}`}
                          className="px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
                        >
                          View Details
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-primary-950 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">
              Don't See the Right Fit?
            </h2>
            <p className="text-lg mb-8 text-primary-100">
              We're always looking for talented individuals who share our passion for innovation. 
              Send us your resume and we'll keep you in mind for future opportunities.
            </p>
            <a
              href="mailto:concierge@24lp.io?subject=Resume%20Submission&body=Please%20attach%20your%20resume%20and%20let%20us%20know%20what%20type%20of%20role%20you%20are%20interested%20in."
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary-950 font-medium rounded-lg hover:bg-gray-100 transition-colors"
            >
              Submit Resume
              <ChevronRightIcon className="h-5 w-5" />
            </a>
          </div>
        </section>
      </div>

      {/* Footer */}
      <Footer />
    </>
  );
};

export default Careers; 